/* eslint-disable react-refresh/only-export-components */
import { createContext, useState, useEffect, ReactNode, useContext, useCallback } from "react";
import SystemMessage from "../components/SystemMessage/Index";
import { useApiData } from "./ApiData";
import { Organization, Profile, SubscriptionData, Subscription } from "@/types";

type OrganizationContextType = {
  organizations: Organization[] | null;
  currentOrganization: string;
  organizationData: Organization | null;
  currentRole: string;
  setCurrentOrganization: (org: string) => void;
  profile?: Profile;
  role?: string;
  subscription: SubscriptionData;
  refreshUser: () => Promise<void>;
  pendingOrganization: string | null;
  setProfile: (profile?: Profile) => void;
};

const OrganizationContext = createContext<OrganizationContextType>({
  organizations: null,
  currentOrganization: "",
  currentRole: "",
  organizationData: null,
  setCurrentOrganization: () => {},
  subscription: {
    status: "none",
    type: "none",
  },
  refreshUser: async () => {},
  pendingOrganization: null,
  setProfile: () => {},
});

export const useOrganizations = () => {
  const { organizations, subscription, currentRole, currentOrganization, setCurrentOrganization } = useContext(OrganizationContext);
  return { organizations, subscription, currentRole, currentOrganization, setCurrentOrganization };
};

export const useOrganizationData = () => {
  const { organizationData } = useContext(OrganizationContext);
  return organizationData;
};

export const useProfile = () => {
  const { profile, subscription } = useContext(OrganizationContext);
  if (!profile) return undefined;
  return { ...profile, subscription: subscription.type, hasValidSubscription: ["trial", "recurring", "org"].includes(subscription.type) };
};

export const useRole = () => {
  const { role } = useContext(OrganizationContext);
  return role;
};

// Name of the organization the user has asked to join, while the owner has not approved it yet
export const usePendingOrganization = () => {
  const { pendingOrganization } = useContext(OrganizationContext);
  return pendingOrganization;
};

export const useRefreshUser = () => {
  const { refreshUser } = useContext(OrganizationContext);
  return refreshUser;
};

export const useSetCurrentOrganization = () => {
  const { currentOrganization, setCurrentOrganization } = useContext(OrganizationContext);
  return { currentOrganization, setCurrentOrganization };
};

type OrganizationProviderProps = {
  children: ReactNode;
};

export const findCurrentSubscription = (subscriptions: Subscription[]): SubscriptionData => {
  const currentSub = subscriptions[0] || {};

  return currentSub
    ? {
        id: currentSub.id,
        type: currentSub.type,
        status: currentSub.status,
        endDate: currentSub.endDate,
        startDate: currentSub.startDate,
      }
    : {
        type: "none" as const,
        status: "none" as const,
      };
};

// 'invited' is a request the owner has not approved yet: it gives no org context, only the name to show
export const orgStateFromUser = (
  organization: Organization | null | undefined,
  role: string | undefined
): { organization: Organization | null; pendingOrganization: string | null } => {
  if (!organization) return { organization: null, pendingOrganization: null };
  if (role === "invited") return { organization: null, pendingOrganization: organization.name ?? "" };
  return { organization, pendingOrganization: null };
};

export const OrganizationProvider: React.FC<OrganizationProviderProps> = ({ children }) => {
  const { get } = useApiData();
  const [organizations, setOrganizations] = useState<Organization[] | null>(null);
  const [currentOrganization, setCurrentOrganization] = useState("");
  const [organizationData, setCurrentOrganizationData] = useState<Organization | null>(null);
  const [currentRole, setCurrentRole] = useState("");
  const [subscription, setSubscription] = useState<SubscriptionData>({} as SubscriptionData);
  const [profile, setProfile] = useState<Profile | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [role, setRole] = useState<string | undefined>(undefined);
  const [pendingOrganization, setPendingOrganization] = useState<string | null>(null);

  const refreshUser = useCallback(async () => {
    const result = await get("/user");
    const { profile, role, subscription: sub } = result || {};
    const { organization, pendingOrganization } = orgStateFromUser(result?.organization, role);

    const statusMap: Record<string, SubscriptionData> = {
      trial: { type: "trial", status: "active" },
      recurring: { type: "recurring", status: "active" },
      org: { type: "org", status: "active" },
      expired: { type: "recurring", status: "expired" },
      cancelled: { type: "recurring", status: "cancelled" },
      orgexpired: { type: "org", status: "expired" },
    };

    if (organization) {
      setOrganizations([organization]);
      setCurrentOrganization(organization.id);
      setCurrentOrganizationData(organization);
      setCurrentRole(role);
    } else {
      setOrganizations(null);
      setCurrentOrganization("");
      setCurrentOrganizationData(null);
      setCurrentRole("");
    }

    setPendingOrganization(pendingOrganization);
    setSubscription(statusMap[sub] ?? { type: "none", status: "none" });

    setProfile(profile);
    setRole(role);
  }, [get]);

  useEffect(() => {
    const load = async () => {
      await refreshUser();
      setIsLoading(false);
    };
    load();
  }, [refreshUser]);

  if (isLoading) {
    return <SystemMessage message='loading' />;
  }

  return (
    <OrganizationContext.Provider
      value={{
        organizations,
        subscription,
        currentOrganization,
        organizationData,
        currentRole,
        setCurrentOrganization,
        profile,
        role,
        refreshUser,
        pendingOrganization,
        setProfile,
      }}
    >
      {children}
    </OrganizationContext.Provider>
  );
};
